import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowRight, CircleDollarSign, Coins, Fuel, Layers, PauseCircle,
  PlayCircle, ShieldAlert, Users,
} from "lucide-react";
import AdminStat from "../AdminStat";
import { setContractPaused } from "../../../lib/investAdminApi";
import {
  ADMIN_LOW_GAS_ETH, CATEGORY_LABEL, ConfirmChainDialog, CopyHex, LOW_GAS_ETH, StatusBadge,
  collectionStatus, fmtEth, fmtNum, fmtUsd, toneStyle,
} from "./bcShared";

function buildAlerts({ overview, collections, orders, withdrawals, wallets }) {
  const alerts = [];
  const contract = overview?.contract;
  if (contract?.paused) {
    alerts.push({
      tone: "danger", icon: PauseCircle, id: "paused",
      title: "Contract is paused",
      body: "Purchases, transfers, withdrawals and minting are blocked until you resume it.",
    });
  }
  if (contract && Number(contract.adminBalanceEth) < ADMIN_LOW_GAS_ETH) {
    alerts.push({
      tone: "warn", icon: Fuel, id: "admin-gas",
      title: "Admin wallet is low on gas",
      body: `${fmtEth(contract.adminBalanceEth)} left. Minting, transfers and investor gas top-ups draw from this wallet.`,
    });
  }
  const offChain = collections.filter((c) => c.status === "MINTED" && c.onChain === false);
  if (offChain.length) {
    alerts.push({
      tone: "danger", icon: ShieldAlert, id: "sync", tab: "collections",
      title: `${offChain.length} collection${offChain.length > 1 ? "s" : ""} missing on-chain`,
      body: "Records say minted but the contract never issued these token IDs — the chain was likely reset. Investors can't buy them.",
    });
  }
  const failedMints = collections.filter((c) => c.status === "MINT_FAILED").length;
  if (failedMints) {
    alerts.push({
      tone: "warn", icon: Layers, id: "mint-failed", tab: "collections",
      title: `${failedMints} collection${failedMints > 1 ? "s" : ""} failed to mint`,
      body: "These were saved but never reached the chain.",
    });
  }
  const failedOrders = orders.filter((o) => o.status === "TRANSFER_FAILED").length;
  if (failedOrders) {
    alerts.push({
      tone: "danger", icon: AlertTriangle, id: "orders", tab: "orders",
      title: `${failedOrders} paid order${failedOrders > 1 ? "s" : ""} failed to transfer`,
      body: "The investor was charged but didn't receive tokens. Review and resolve manually.",
    });
  }
  const failedWd = withdrawals.filter((w) => w.status === "FAILED").length;
  if (failedWd) {
    alerts.push({
      tone: "warn", icon: AlertTriangle, id: "wd", tab: "withdrawals",
      title: `${failedWd} withdrawal${failedWd > 1 ? "s" : ""} failed`,
      body: "Tokens stayed in the investor's custodial wallet.",
    });
  }
  const lowGas = wallets.filter((w) => w.ethBalance != null && Number(w.ethBalance) < LOW_GAS_ETH).length;
  if (lowGas) {
    alerts.push({
      tone: "warn", icon: Fuel, id: "wallet-gas", tab: "wallets",
      title: `${lowGas} investor wallet${lowGas > 1 ? "s" : ""} low on gas`,
      body: "They'll be topped up automatically on their next withdrawal, or you can top up now.",
    });
  }
  return alerts;
}

function SupplyBar({ sold, total }) {
  const pct = total ? Math.min(100, (sold / total) * 100) : 0;
  return (
    <div className="h-2 rounded-full bg-[var(--zc-bg-3)] overflow-hidden" role="meter" aria-valuemin={0} aria-valuemax={total || 0} aria-valuenow={sold} aria-label="Tokens sold">
      <div className="h-full rounded-full bg-[var(--zc-secondary)]" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function BlockchainOverview({ overview, collections, orders, withdrawals, wallets, onNavigate, onChanged }) {
  const [confirmPause, setConfirmPause] = useState(false);
  const [busy, setBusy] = useState(false);
  const contract = overview?.contract;

  const alerts = useMemo(
    () => buildAlerts({ overview, collections, orders, withdrawals, wallets }),
    [overview, collections, orders, withdrawals, wallets],
  );

  const kpis = useMemo(() => {
    const done = orders.filter((o) => o.status === "COMPLETED");
    return {
      gross: done.reduce((s, o) => s + (o.fees?.totalCents || 0), 0),
      fees: done.reduce((s, o) => s + (o.fees?.platformFeeCents || 0) + (o.fees?.transactionFeeCents || 0), 0),
      sold: done.reduce((s, o) => s + (o.quantity || 0), 0),
      investors: new Set(done.map((o) => o.userId)).size,
    };
  }, [orders]);

  const live = collections.filter((c) => c.status === "MINTED");

  const togglePause = async () => {
    setBusy(true);
    try {
      await setContractPaused(!contract.paused);
      toast.success(contract.paused ? "Contract resumed" : "Contract paused");
      setConfirmPause(false);
      onChanged();
    } catch (e) {
      toast.error(e.message || "Transaction failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="bc-overview">
      {alerts.length > 0 && (
        <div className="zc-card overflow-hidden" data-testid="bc-alerts">
          <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">
            Needs attention · {alerts.length}
          </div>
          {alerts.map((a) => (
            <div key={a.id} className="px-4 sm:px-6 py-3.5 border-b border-[var(--zc-border)] last:border-0 flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={toneStyle(a.tone)}>
                <a.icon size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-[var(--zc-text-2)] mt-0.5">{a.body}</div>
              </div>
              {a.tab && (
                <button type="button" onClick={() => onNavigate(a.tab)} className="zc-btn-ghost px-3 h-8 text-xs font-semibold inline-flex items-center gap-1 shrink-0">
                  Review <ArrowRight size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <AdminStat label="Gross sales" v={fmtUsd(kpis.gross)} icon={<CircleDollarSign size={14} />} />
        <AdminStat label="Fee revenue" v={fmtUsd(kpis.fees)} icon={<Coins size={14} />} />
        <AdminStat label="Tokens sold" v={fmtNum(kpis.sold)} icon={<Layers size={14} />} onClick={() => onNavigate("orders")} />
        <AdminStat label="Investors" v={fmtNum(kpis.investors)} icon={<Users size={14} />} onClick={() => onNavigate("wallets")} />
      </div>

      <div className="grid lg:grid-cols-[1.4fr,1fr] gap-4">
        <div className="zc-card overflow-hidden">
          <div className="px-4 sm:px-6 py-3 border-b border-[var(--zc-border)] flex items-center justify-between gap-3">
            <div className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Supply by collection</div>
            <button type="button" onClick={() => onNavigate("collections")} className="text-xs font-semibold text-[var(--zc-secondary)] inline-flex items-center gap-1 hover:underline">
              Manage <ArrowRight size={12} />
            </button>
          </div>
          {live.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-[var(--zc-text-3)]">No live collections yet.</div>
          )}
          {live.map((c) => {
            const sold = (c.totalSupply || 0) - (c.availableForSale || 0);
            const st = collectionStatus(c);
            return (
              <div key={c.id} className="px-4 sm:px-6 py-3.5 border-b border-[var(--zc-border)] last:border-0">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{c.name}</span>
                    <span className="text-2xs font-mono text-[var(--zc-text-3)]">{c.symbol}</span>
                    {st.tone !== "success" && <StatusBadge tone={st.tone}>{st.label}</StatusBadge>}
                  </div>
                  <div className="text-xs text-[var(--zc-text-2)] tabular-nums shrink-0">
                    <b className="text-[var(--zc-text)]">{fmtNum(sold)}</b> / {fmtNum(c.totalSupply)} sold
                  </div>
                </div>
                <SupplyBar sold={sold} total={c.totalSupply} />
                <div className="text-2xs text-[var(--zc-text-3)] mt-1.5">
                  {CATEGORY_LABEL[c.category] || c.category} · {fmtUsd(c.unitPriceCents)} / token · token #{c.tokenId}
                </div>
              </div>
            );
          })}
        </div>

        <div className="zc-card p-5" data-testid="bc-contract-card">
          <div className="text-xs uppercase tracking-widest text-[var(--zc-text-3)] font-semibold">Smart contract</div>
          {!contract ? (
            <div className="text-sm text-[var(--zc-text-3)] mt-4">{overview?.contractError || "Contract details unavailable."}</div>
          ) : (
            <>
              <dl className="mt-4 space-y-3 text-sm">
                {[
                  ["Contract", <CopyHex value={contract.contractAddress} label="Contract address" />],
                  ["Owner / admin", <CopyHex value={contract.adminAddress} label="Admin address" />],
                  ["Admin gas", <span className="tabular-nums">{fmtEth(contract.adminBalanceEth)}</span>],
                  ["Gas price", <span className="tabular-nums">{Number(contract.gasPriceGwei).toLocaleString("en-US", { maximumFractionDigits: 3 })} gwei</span>],
                  ["Collections issued", <span className="tabular-nums">{Math.max(0, contract.nextCollectionId - 1)}</span>],
                  ["State", contract.paused
                    ? <StatusBadge tone="danger" icon={PauseCircle}>Paused</StatusBadge>
                    : <StatusBadge tone="success" icon={PlayCircle}>Active</StatusBadge>],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <dt className="text-[var(--zc-text-3)] text-xs">{k}</dt>
                    <dd className="text-right">{v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-5 pt-4 border-t border-[var(--zc-border)]">
                <div className="text-xs font-semibold">{contract.paused ? "Resume contract" : "Emergency stop"}</div>
                <p className="text-xs text-[var(--zc-text-3)] mt-1">
                  {contract.paused
                    ? "Re-enable purchases, transfers, withdrawals and minting."
                    : "Freezes every token transfer and mint across all collections. Use only for incidents."}
                </p>
                <button
                  type="button"
                  onClick={() => setConfirmPause(true)}
                  className={(contract.paused ? "zc-btn-secondary" : "zc-btn-outline-primary") + " mt-3 px-4 h-9 text-xs font-semibold inline-flex items-center gap-1.5"}
                  data-testid="bc-pause-toggle"
                >
                  {contract.paused ? <PlayCircle size={14} /> : <PauseCircle size={14} />}
                  {contract.paused ? "Resume contract" : "Pause contract"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {contract && (
        <ConfirmChainDialog
          open={confirmPause}
          onOpenChange={setConfirmPause}
          title={contract.paused ? "Resume the contract?" : "Pause the contract?"}
          description={contract.paused
            ? "Investors will be able to buy and withdraw tokens again immediately."
            : "All purchases, transfers, withdrawals and minting will fail until you resume. Orders paid during a pause will fail to transfer."}
          confirmLabel={contract.paused ? "Resume contract" : "Pause contract"}
          tone={contract.paused ? "primary" : "danger"}
          busy={busy}
          onConfirm={togglePause}
        />
      )}
    </div>
  );
}
